import {
    Grid,
    InputBase,
    InputBaseComponentProps,
    SxProps,
    Theme,
    Tooltip,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import React, {
    CSSProperties,
    MouseEventHandler,
    ReactNode,
    useRef,
} from "react";
import AcxInputLabel from "./AcxInputLabel";
import { useTheme } from "@mui/material";
import { sxClsx } from "utils/styleClsx";

/**
 * Props for the AcxMainTextFieldGrid component.
 *
 * @property {SxProps<any>} [textContainerSx] - Additional styles to apply to the text field container, merged with the base container styles.
 * @property {SxProps<any>} [textContainerSxOverride] - Styles to apply to the text field container, overriding and disabling the base container styles (such as borders).
 */
export interface IAcxTextFieldProps {
    labelText?: string | undefined;
    error?: boolean;
    placeholderText?: string;
    id: string;
    value?: string;
    multiLine?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    onEnterPressed?: () => void;
    helperText?: string | undefined;
    inputProps?: InputBaseComponentProps;
    required?: boolean;
    type?:
        | string
        | ("text" | "time" | "url" | "tel" | "number" | "email" | "date");
    onChange?: (evt: React.ChangeEvent<HTMLInputElement>) => void;
    containerStyle?: CSSProperties;
    containerSx?: SxProps<any>;
    textItemStyle?: CSSProperties;
    textContainerSx?: SxProps<any>;
    textContainerSxOverride?: SxProps<any>;
    labelSx?: SxProps<any>;
    sx?: SxProps<any>;
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
    autofocus?: boolean;
    onClick?: MouseEventHandler<any>;
    onDblClick?: MouseEventHandler<any>;
    rows?: number;
    rowsMax?: number;
    disabled?: boolean;
    autoFocus?: boolean;
    blurOnEnterPress?: boolean;
    autoComplete?: string;
}

function InputWarningIcon(props: { title: string | undefined }) {
    return (
        <Tooltip title={props.title ?? ""} placement={"top-end"}>
            <WarningIcon
                sx={{
                    color: (theme) => theme.palette.red.main,
                    marginRight: "8px",
                    marginLeft: "4px",
                }}
            />
        </Tooltip>
    );
}

export default function AcxMainTextField({
    labelText = "",
    error = false,
    placeholderText = "",
    value = "",
    required = false,
    type = "text",
    ...props
}: IAcxTextFieldProps) {
    const [focused, setFocused] = React.useState(false);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const theme = useTheme();
    const onBlur = () => {
        setFocused(false);
        props.onBlur?.();
    };

    const onFocus = () => {
        setFocused(true);
        props.onFocus?.();
    };

    const onKeyPress = (
        e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        if (e.key === "Enter") {
            props.onEnterPressed?.();
        }
        if (props.blurOnEnterPress && e.key === "Enter") {
            onBlur();
            inputRef.current?.blur();
        }
    };

    // Styles for label
    const labelSx = {
        fontFamily:
            theme.typography.fontFamily ??
            "Roboto, Helvetica, Arial, sans-serif",
        color: focused
            ? theme.palette.secondary.main
            : theme.palette.text.primary,
        fontSize: "12px",
        lineHeight: "16px",
        userSelect: "none",
        opacity: 1,
        ...(required && !value
            ? {
                  "&:after": {
                      color: theme.palette.red.main,
                      content: `" *"`,
                  },
              }
            : {}),
        ...props.labelSx,
    } as SxProps<Theme>;

    // Styles for input container
    const textContainerSx = sxClsx(
        !props.textContainerSxOverride && {
            border: "1px solid",
            backgroundColor: theme.palette.white.main,
            borderColor: focused
                ? theme.palette.primary.main
                : theme.palette.lightgrayBorder.main,
            borderRadius: 1,
            alignItems: "center",
        },
        props.textContainerSx,
    );
    // Styles for input text
    const inputTextSx = sxClsx(
        {
            border: "none",
            borderWidth: 0,
            outlineWidth: 0,
            backgroundColor: "transparent",
            fontSize: "13px",
            lineHeight: "20px",
            fontFamily: theme.typography.fontFamily,
            paddingLeft: theme.spacing(1),
            userSelect: "text",
            caretColor: focused ? theme.palette.primary.main : "transparent",
            borderColor: focused ? theme.palette.info.light : "transparent",
            borderRadius: focused ? theme.shape.borderRadius : "0",
        },
        props.sx,
    );

    return (
        <Grid
            container
            style={props.containerStyle}
            sx={props.containerSx}
            direction="column"
            justifyContent="center"
            alignItems="stretch"
        >
            {!!labelText && (
                <Grid item>
                    <AcxInputLabel
                        htmlFor={props.id}
                        id={props.id}
                        labelText={labelText}
                        focused={focused}
                        sx={labelSx}
                    ></AcxInputLabel>
                </Grid>
            )}

            <Grid
                style={{ ...(props.textItemStyle ?? {}), userSelect: "text" }}
                sx={textContainerSx}
                item
            >
                <InputBase
                    id={props.id}
                    autoComplete={props.autoComplete}
                    onDoubleClick={props.onDblClick}
                    disabled={props.disabled}
                    readOnly={props.disabled}
                    onClick={props.onClick}
                    inputProps={{
                        ...{ type },
                        ...props.inputProps,
                    }}
                    placeholder={placeholderText}
                    startAdornment={error ? undefined : props.startAdornment}
                    endAdornment={
                        error ? (
                            <InputWarningIcon title={props.helperText} />
                        ) : (
                            props.endAdornment
                        )
                    }
                    type={type}
                    fullWidth
                    minRows={props.rows}
                    maxRows={props.rowsMax}
                    multiline={props.multiLine}
                    error={error}
                    value={value}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onChange={props.onChange}
                    autoFocus={props.autoFocus ?? false}
                    inputRef={inputRef}
                    onKeyDown={onKeyPress}
                    sx={inputTextSx}
                />
            </Grid>
        </Grid>
    );
}
